import { Fragment, useEffect, useState } from 'react';
import { Transition } from '@headlessui/react';
import { ResizableTextArea } from '../resizableTextArea/ResizableTextArea';

interface Props {
  data: any[];
  name?: string;
  onChange: (value: any) => void;
  onQueryChange?: (value: any) => void;
  onFocus?: (value?: any) => void;
  onSelected?: (value: any) => void;
  labelKey: string;
  valueKey: string;
  value?: any;
  isReadOnly?: boolean;
  placeholder?: string;
  className?: string;
  forceSelect?: boolean;
  displayPattern?: string;
  displayValue?: string;
  rows?: number;
  resize?: boolean;
  isFormField?: boolean;
  label?: string;
  searchWhileEmpty?: boolean;
  autofocus?: boolean;
}

/**
 * Autocomplete component
 */
export default function SelectAutocomplete({
  data,
  name,
  onChange,
  onQueryChange,
  onFocus,
  onSelected,
  value,
  labelKey,
  valueKey,
  isReadOnly,
  placeholder,
  className,
  forceSelect,
  displayPattern,
  displayValue,
  rows,
  resize = true,
  isFormField,
  label,
  searchWhileEmpty,
  autofocus,
}: Props) {
  const [localData, setLocalData] = useState<any[]>(data);
  const [selected, setSelected] = useState<any>();
  const [hasFocus, setHasFocus] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>(value ?? '');
  const [query, setQuery] = useState<string>('');
  const [debouncedValue, setDebouncedValue] = useState<string>('');

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const filteredItems =
    query && query === ''
      ? localData
      : localData.filter(
          (item) =>
            (item[labelKey] !== null &&
              item[labelKey] !== undefined &&
              item[labelKey]
                .toLowerCase()
                .replace(/\s+/g, '')
                .includes(query.toString().toLowerCase().replace(/\s+/g, ''))) ||
            (valueKey &&
              item[valueKey] !== null &&
              item[valueKey] !== undefined &&
              item[valueKey]
                .toLowerCase()
                .replace(/\s+/g, '')
                .includes(query.toString().toLowerCase().replace(/\s+/g, ''))),
        );

  /**
   * Selects item, sets input value
   */
  const handleSelect = (opt: any) => {
    setInputValue(displayValue ? opt[displayValue] : opt[labelKey]);
    setSelected(opt);
    setIsOpen(false);
    onSelected && onSelected(opt);
  };

  useEffect(() => {
    if (inputValue === '' && selected && selected[labelKey] !== undefined) setSelected({});
    if (selected && selected[labelKey] === inputValue) {
      onChange(selected);
    }
  }, [selected, inputValue]);

  useEffect(() => {
    if (!value) return setInputValue('');
    const tmpInputVal = localData.find((x: any) => x[valueKey] === value);
    // item found in localData
    if (value && tmpInputVal) {
      setInputValue(value);
      handleSelect(localData[localData.findIndex((x: any) => x[valueKey] === value)]);
    }
    // item not found - show textBox
    if (value && !tmpInputVal) {
      setQuery(value);
      setInputValue(value);
    }
  }, [value]);

  // show filtered values by pattern
  const getNameByPattern = (item: any) => {
    if (!displayPattern) return;
    let name = '';
    name += displayPattern
      .split('-')
      .map((attr: string, index: number) => {
        if (!item[attr]) return;
        return index === 0 ? item[attr] : ' - ' + item[attr];
      })
      .join(' ');
    name = name.replace(',', '');
    return name.split(' - ')[0] === ' ' ? name.replace(' - ', '') : name;
  };

  useEffect(() => {
    if (filteredItems.length > 0) setIsOpen(true);
    if (filteredItems.length === 0) setIsOpen(false);
  }, [query, filteredItems]);

  /**
   * Delay for 500ms before api call
   */
  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedValue(inputValue), 500);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [inputValue]);

  /**
   * Call api, reset delay
   */
  useEffect(() => {
    onChange(inputValue.trim());
    if (!inputValue || !debouncedValue || !hasFocus || !onQueryChange) return;
    onQueryChange(inputValue.trim());
    setDebouncedValue('');
  }, [inputValue, debouncedValue]);

  return (
    <div className="w-full h-full">
      <div className="h-auto">
        <ResizableTextArea
          autofocus={autofocus}
          name={name}
          isFormField={isFormField}
          label={label}
          className={`border border-gray-300 pl-2 w-full rounded-md appearance-none block placeholder-gray-500 text-gray-900 focus:outline-none resize-none overflow-hidden ${className}`}
          placeholder={placeholder}
          value={inputValue}
          resize={resize}
          onFocus={() => {
            onFocus && onFocus();
            setHasFocus(true);
            setIsOpen(true);
          }}
          onBlur={() => {
            if (forceSelect && selected === undefined) setInputValue('');
            setHasFocus(false);
          }}
          onChange={(e: string) => {
            setInputValue(e);
            setQuery(e);
          }}
          isRequired={isReadOnly}
          rows={rows ?? 1}
        />
        <Transition
          as={Fragment}
          show={filteredItems.length > 0 && isOpen && hasFocus}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setQuery('')}
        >
          <ul className="absolute mt-1 max-h-60 w-62 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-30">
            {filteredItems.length === 0 && forceSelect ? (
              <li
                className={`relative cursor-default select-none py-2 pl-4 pr-4 hover:bg-gray-200 text-gray-900`}
              >
                <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                  No results found
                </span>
              </li>
            ) : query.length === 0 && searchWhileEmpty ? (
              data.length > 0 &&
              data.map((item, index: number) => (
                <li
                  key={index + '_' + item.id}
                  className={`relative cursor-default select-none py-2 pl-4 pr-4 hover:bg-gray-200 text-gray-900`}
                  value={item}
                  onClick={() => handleSelect(item)}
                  // fix for touchpad event
                  onMouseDown={() => handleSelect(item)}
                >
                  <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                    {!displayPattern &&
                      `${item[labelKey]}${displayValue ? ' - ' + item[valueKey] : ''}`}
                    {displayPattern && getNameByPattern(item)}
                  </span>
                </li>
              ))
            ) : (
              filteredItems.length > 0 &&
              filteredItems.map((item, index: number) => (
                <li
                  key={index + '_' + item.id}
                  className={`relative cursor-default select-none py-2 pl-4 pr-4 hover:bg-gray-200 text-gray-900`}
                  value={item}
                  onClick={() => handleSelect(item)}
                  // fix for touchpad event
                  onMouseDown={() => handleSelect(item)}
                >
                  <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                    {!displayPattern &&
                      `${item[labelKey]}${displayValue ? ' - ' + item[valueKey] : ''}`}
                    {displayPattern && getNameByPattern(item)}
                  </span>
                </li>
              ))
            )}
          </ul>
        </Transition>
      </div>
    </div>
  );
}
